import type { PaymentMethodSummary } from './types.ts'

const BRANDS: Record<string, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  'american-express': 'American Express',
  discover: 'Discover',
  'diners-club': 'Diners Club',
  jcb: 'JCB'
}

/**
 * Names a payment method for display, without the expiry.
 * @param method - The brand, type and last four.
 * @returns For example "Visa ending in 4242".
 */
export function describeMethod(
  method: Pick<PaymentMethodSummary, 'brand' | 'last4' | 'paymentType'>
): string {
  if (method.paymentType === 'ach')
    return `Bank account ending in ${method.last4}`
  const brand = method.brand
    ? (BRANDS[method.brand.toLowerCase()] ?? method.brand)
    : 'Card'
  return `${brand} ending in ${method.last4}`
}

/**
 * Formats a card expiry for display.
 * @param expMonth - The month, 1 to 12.
 * @param expYear - The four-digit year.
 * @returns For example "Expires 01/2031", or an empty string when unknown.
 */
export function describeExpiry(
  expMonth: number | null,
  expYear: number | null
): string {
  if (expMonth === null || expYear === null) return ''
  return `Expires ${String(expMonth).padStart(2, '0')}/${expYear}`
}

/**
 * Formats an amount in minor units, such as cents, as a currency.
 * @param minor - The amount in the currency's minor unit.
 * @param currency - The ISO 4217 code.
 * @param locale - The session locale.
 * @returns For example "$25.75".
 */
export function formatMoney(
  minor: number,
  currency: string,
  locale: string
): string {
  let format: Intl.NumberFormat
  try {
    format = new Intl.NumberFormat(locale, { style: 'currency', currency })
  } catch {
    format = new Intl.NumberFormat('en-US', { style: 'currency', currency })
  }
  const digits = format.resolvedOptions().maximumFractionDigits ?? 2
  return format.format(minor / 10 ** digits)
}
